import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Row,
  Col,
  Card,
  Button,
  Spin,
  Alert,
  DatePicker,
  Select,
  Input,
  Modal,
  message,
  Tag,
  Descriptions,
  Divider,
  Rate,
  Typography
} from 'antd';
import {
  EnvironmentOutlined,
  ClockCircleOutlined,
  EuroOutlined,
  CalendarOutlined,
  UserOutlined,
  ArrowLeftOutlined,
  StarOutlined,
  QuestionCircleOutlined,
  ShoppingOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useStores } from '../../stores';
import ReviewList from '../../components/reviews/ReviewList';
import FavoriteButton from '../../components/favorites/FavoriteButton';
import InquiryModal from '../../components/inquiry/InquiryModal';
import './ServiceDetailPage.scss';

const { Title, Text } = Typography;

const { TextArea } = Input;
const { Option } = Select;

const ServiceDetailPage = observer(() => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { serviceStore, appointmentStore, authStore, petStore } = useStores();

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedPet, setSelectedPet] = useState(null);
  const [customerNotes, setCustomerNotes] = useState('');
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [booking, setBooking] = useState(false);
  const [inquiryModalVisible, setInquiryModalVisible] = useState(false);
  const [purchaseModalVisible, setPurchaseModalVisible] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    // Fetch service details
    serviceStore.fetchServiceDetail(id);

    // Clear previously loaded slots
    serviceStore.clearAvailableSlots();

    // Fetch user's pets if authenticated
    if (authStore.isAuthenticated) {
      petStore.fetchMyPets();
    }

    return () => {
      serviceStore.clearAvailableSlots();
    };
  }, [id, authStore.isAuthenticated]);

  const handleDateChange = async (date) => {
    if (!date) {
      setSelectedDate(null);
      setSelectedSlot(null);
      serviceStore.clearAvailableSlots();
      return;
    }

    setSelectedDate(date);
    setSelectedSlot(null);

    // Fetch available slots for selected date
    const dateString = date.format('YYYY-MM-DD');
    await serviceStore.fetchAvailableSlots(id, dateString);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleBookAppointment = () => {
    if (!authStore.isAuthenticated) {
      message.warning(t('services.detail.loginRequired'));
      navigate('/login');
      return;
    }

    if (!selectedDate || !selectedSlot) {
      message.warning(t('services.detail.selectDateAndSlot'));
      return;
    }

    setBookingModalVisible(true);
  };

  const confirmBooking = async () => {
    setBooking(true);

    const appointmentData = {
      service: parseInt(id),
      appointment_date: selectedDate.format('YYYY-MM-DD'),
      start_time: selectedSlot.time,
      customer_notes: customerNotes
    };

    if (selectedPet) {
      appointmentData.pet = selectedPet;
    }

    const result = await appointmentStore.createAppointment(appointmentData);

    setBooking(false);

    if (result.success) {
      message.success(t('services.detail.bookingSuccess'));
      setBookingModalVisible(false);
      navigate('/my-appointments');
    } else {
      // Display detailed error message
      let errorMessage = t('services.detail.bookingFailed');
      if (typeof result.error === 'object') {
        errorMessage = Object.values(result.error).flat().join(', ');
      } else {
        errorMessage = result.error;
      }
      message.error(errorMessage);
    }
  };

  const disabledDate = (current) => {
    // Disable dates before today
    return current && current < dayjs().startOf('day');
  };

  const handlePurchaseService = () => {
    if (!authStore.isAuthenticated) {
      message.warning(t('services.detail.loginRequired'));
      navigate('/login');
      return;
    }
    setPurchaseModalVisible(true);
  };

  const confirmPurchase = async () => {
    setPurchasing(true);

    // Create an appointment without specific time slot for non-booking services
    const appointmentData = {
      service: parseInt(id),
      appointment_date: dayjs().format('YYYY-MM-DD'),
      start_time: '00:00',
      customer_notes: customerNotes,
      is_service_request: true  // Flag to indicate this is a service request, not a time-based appointment
    };

    if (selectedPet) {
      appointmentData.pet = selectedPet;
    }

    const result = await appointmentStore.createAppointment(appointmentData);

    setPurchasing(false);

    if (result.success) {
      message.success(t('services.detail.purchaseSuccess'));
      setPurchaseModalVisible(false);
      navigate('/my-appointments');
    } else {
      let errorMessage = t('services.detail.purchaseFailed');
      if (typeof result.error === 'object') {
        errorMessage = Object.values(result.error).flat().join(', ');
      } else {
        errorMessage = result.error;
      }
      message.error(errorMessage);
    }
  };

  if (serviceStore.loading && !serviceStore.selectedService) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  if (!serviceStore.selectedService) {
    return (
      <div className="service-detail-page">
        <Alert message={t('services.detail.notFound')} type="error" />
      </div>
    );
  }

  const service = serviceStore.selectedService;
  const serviceName = i18n.language === 'el' ? (service.name_el || service.name_en) : service.name_en;
  const serviceDescription = i18n.language === 'el' ? (service.description_el || service.description_en) : service.description_en;
  const categoryName = i18n.language === 'el' ? (service.category_name_el || service.category_name_en) : service.category_name_en;

  return (
    <div className="service-detail-page">
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/services')}
        className="back-button"
      >
        {t('services.detail.backToServices')}
      </Button>

      {serviceStore.error && (
        <Alert
          message={t('common.error')}
          description={serviceStore.error}
          type="error"
          closable
          onClose={() => serviceStore.clearError()}
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={14}>
          <Card className="service-info-card">
            <div className="service-header">
              <Tag color="blue" className="category-tag">
                {categoryName}
              </Tag>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
                <h1 className="service-title" style={{ marginBottom: 0 }}>{serviceName}</h1>
                <FavoriteButton
                  type="service"
                  itemId={parseInt(id)}
                  showText
                  size="large"
                />
              </div>
              {service.average_rating > 0 && (
                <div className="service-rating" style={{ margin: '12px 0' }}>
                  <Rate disabled allowHalf value={parseFloat(service.average_rating)} />
                  <Text style={{ marginLeft: 8 }}>
                    {parseFloat(service.average_rating).toFixed(1)} ({t('services.detail.reviews', { count: service.rating_count })})
                  </Text>
                </div>
              )}
              <p className="service-description">{serviceDescription}</p>
            </div>

            <Descriptions bordered column={1} className="service-details">
              <Descriptions.Item label={<><EuroOutlined /> {t('common.price')}</>}>
                <span className="price-value">€{service.price}</span>
              </Descriptions.Item>
              {service.requires_booking !== false && (
                <>
                  <Descriptions.Item label={<><ClockCircleOutlined /> {t('services.detail.duration')}</>}>
                    {service.duration} {service.duration_unit === 'HOURS' ? t('common.hours') : service.duration_unit === 'DAYS' ? t('common.days') : t('services.detail.minutes')}
                  </Descriptions.Item>
                  <Descriptions.Item label={<><UserOutlined /> {t('services.detail.capacity')}</>}>
                    {service.capacity} {service.capacity === 1 ? t('services.detail.person') : t('services.detail.people')} {t('services.detail.perSlot')}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label={<><EnvironmentOutlined /> {t('services.detail.provider')}</>}>
                <div>
                  <div>
                    <a
                      onClick={() => navigate(`/partners/${service.partner}`)}
                      style={{ fontWeight: 'bold', cursor: 'pointer', color: '#9FB18C' }}
                    >
                      {service.partner_name}
                    </a>
                  </div>
                  <div className="address-text">
                    {service.partner_address?.address}<br />
                    {service.partner_address?.city}, {service.partner_address?.postal_code}
                  </div>
                </div>
              </Descriptions.Item>
            </Descriptions>

            {service.requires_booking !== false && service.availabilities && service.availabilities.length > 0 && (
              <div className="working-hours">
                <h3>{t('services.detail.workingHours')}</h3>
                <div className="hours-list">
                  {service.availabilities.map((availability) => (
                    <div key={availability.id} className="hour-item">
                      <span className="day-name">{availability.day_name}</span>
                      <span className="time-range">
                        {availability.start_time.slice(0, 5)} - {availability.end_time.slice(0, 5)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          {service.requires_booking !== false ? (
            // Services that require booking - show date/time selection
            <Card className="booking-card" title={<><CalendarOutlined /> {t('services.detail.bookAppointment')}</>}>
              <div className="booking-form">
                <div className="form-group">
                  <label>{t('services.detail.selectDate')}</label>
                  <DatePicker
                    value={selectedDate}
                    onChange={handleDateChange}
                    disabledDate={disabledDate}
                    format="MMMM D, YYYY"
                    size="large"
                    style={{ width: '100%' }}
                    placeholder={t('services.detail.chooseDate')}
                  />
                </div>

                {serviceStore.loading && selectedDate && (
                  <div className="loading-slots">
                    <Spin /> {t('services.detail.loadingSlots')}
                  </div>
                )}

                {serviceStore.availableSlots && serviceStore.availableSlots.available_slots && (
                  <div className="form-group">
                    <label>{t('services.detail.availableSlots')}</label>
                    {serviceStore.availableSlots.available_slots.length === 0 ? (
                      <Alert
                        message={t('services.detail.noSlots')}
                        description={serviceStore.availableSlots.message || t('services.detail.noSlotsForDate')}
                        type="info"
                      />
                    ) : (
                      <div className="time-slots">
                        {serviceStore.availableSlots.available_slots.map((slot) => (
                          <Button
                            key={slot.time}
                            className={`time-slot ${selectedSlot?.time === slot.time ? 'selected' : ''}`}
                            onClick={() => handleSlotSelect(slot)}
                          >
                            {slot.time}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {selectedDate && selectedSlot && (
                  <>
                    <div className="booking-summary">
                      <h4>{t('services.detail.bookingSummary')}</h4>
                      <div className="summary-item">
                        <span>{t('services.detail.date')}:</span>
                        <span>{selectedDate.format('MMMM D, YYYY')}</span>
                      </div>
                      <div className="summary-item">
                        <span>{t('services.detail.time')}:</span>
                        <span>{selectedSlot.time}</span>
                      </div>
                      <div className="summary-item total">
                        <span>{t('services.detail.totalPrice')}:</span>
                        <span>€{service.price}</span>
                      </div>
                    </div>

                    <Button
                      type="primary"
                      size="large"
                      block
                      onClick={handleBookAppointment}
                    >
                      {t('services.detail.bookAppointment')}
                    </Button>
                  </>
                )}

                <Divider />

                <Button
                  size="large"
                  icon={<QuestionCircleOutlined />}
                  onClick={() => setInquiryModalVisible(true)}
                  block
                >
                  {t('services.detail.askQuestion')}
                </Button>
              </div>
            </Card>
          ) : (
            // Services that don't require booking - show purchase/request option
            <Card className="booking-card" title={<><ShoppingOutlined /> {t('services.detail.requestService')}</>}>
              <div className="booking-form">
                <Alert
                  message={t('services.detail.noBookingRequired')}
                  description={t('services.detail.noBookingRequiredDesc')}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <div className="booking-summary">
                  <h4>{t('services.detail.serviceSummary')}</h4>
                  <div className="summary-item">
                    <span>{t('services.detail.service')}:</span>
                    <span>{serviceName}</span>
                  </div>
                  <div className="summary-item">
                    <span>{t('services.detail.provider')}:</span>
                    <span>{service.partner_name}</span>
                  </div>
                  <div className="summary-item total">
                    <span>{t('common.price')}:</span>
                    <span>€{service.price}</span>
                  </div>
                </div>

                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<ShoppingOutlined />}
                  onClick={handlePurchaseService}
                  style={{ marginBottom: 16 }}
                >
                  {t('services.detail.requestService')}
                </Button>

                <Button
                  size="large"
                  icon={<MessageOutlined />}
                  onClick={() => setInquiryModalVisible(true)}
                  block
                >
                  {t('services.detail.contactProvider')}
                </Button>
              </div>
            </Card>
          )}
        </Col>
      </Row>

      {/* Reviews Section */}
      <Divider style={{ marginTop: 32 }} />
      <div className="reviews-section" style={{ marginTop: 32 }}>
        <Title level={3}>
          <StarOutlined /> {t('services.detail.customerReviews')}
        </Title>
        <ReviewList type="service" itemId={id} />
      </div>

      <Modal
        title={t('services.detail.confirmAppointment')}
        open={bookingModalVisible}
        onOk={confirmBooking}
        onCancel={() => setBookingModalVisible(false)}
        confirmLoading={booking}
        okText={t('services.detail.confirmBooking')}
        width={500}
      >
        <div className="booking-modal-content">
          <p><strong>{t('services.detail.service')}:</strong> {serviceName}</p>
          <p><strong>{t('services.detail.date')}:</strong> {selectedDate?.format('MMMM D, YYYY')}</p>
          <p><strong>{t('services.detail.time')}:</strong> {selectedSlot?.time}</p>
          <p><strong>{t('services.detail.duration')}:</strong> {service.duration} {t('services.detail.minutes')}</p>
          <p><strong>{t('common.price')}:</strong> €{service.price}</p>

          <div className="form-group" style={{ marginTop: 16 }}>
            <label>{t('services.detail.selectAnimal')}</label>
            <Select
              placeholder={t('services.detail.selectAnimalPlaceholder')}
              size="large"
              style={{ width: '100%' }}
              allowClear
              value={selectedPet}
              onChange={setSelectedPet}
              loading={petStore.loading}
            >
              {petStore.myPets.map((pet) => (
                <Option key={pet.id} value={pet.id}>
                  {pet.name} ({pet.animal_category_name})
                </Option>
              ))}
            </Select>
            {petStore.myPets.length === 0 && (
              <small style={{ color: '#888', marginTop: 8, display: 'block' }}>
                {t('services.detail.noAnimals')} <a onClick={() => navigate('/pets/add')} style={{ cursor: 'pointer' }}>{t('services.detail.addAnimal')}</a> {t('services.detail.toProfile')}.
              </small>
            )}
          </div>

          <div className="form-group">
            <label>{t('services.detail.notes')}</label>
            <TextArea
              rows={3}
              placeholder={t('services.detail.notesPlaceholder')}
              value={customerNotes}
              onChange={(e) => setCustomerNotes(e.target.value)}
              maxLength={500}
            />
          </div>
        </div>
      </Modal>

      {/* Inquiry Modal */}
      <InquiryModal
        visible={inquiryModalVisible}
        onClose={() => setInquiryModalVisible(false)}
        inquiryType="SERVICE"
        serviceId={parseInt(id)}
        serviceName={serviceName}
        partnerId={service.partner}
        partnerName={service.partner_name}
      />

      {/* Purchase Modal for non-booking services */}
      <Modal
        title={t('services.detail.confirmRequest')}
        open={purchaseModalVisible}
        onOk={confirmPurchase}
        onCancel={() => setPurchaseModalVisible(false)}
        confirmLoading={purchasing}
        okText={t('services.detail.confirmPurchase')}
        width={500}
      >
        <div className="booking-modal-content">
          <p><strong>{t('services.detail.service')}:</strong> {serviceName}</p>
          <p><strong>{t('services.detail.provider')}:</strong> {service.partner_name}</p>
          <p><strong>{t('common.price')}:</strong> €{service.price}</p>

          <Alert
            message={t('services.detail.purchaseNote')}
            description={t('services.detail.purchaseNoteDesc')}
            type="info"
            showIcon
            style={{ margin: '16px 0' }}
          />

          <div className="form-group" style={{ marginTop: 16 }}>
            <label>{t('services.detail.selectAnimal')}</label>
            <Select
              placeholder={t('services.detail.selectAnimalPlaceholder')}
              size="large"
              style={{ width: '100%' }}
              allowClear
              value={selectedPet}
              onChange={setSelectedPet}
              loading={petStore.loading}
            >
              {petStore.myPets.map((pet) => (
                <Option key={pet.id} value={pet.id}>
                  {pet.name} ({pet.animal_category_name})
                </Option>
              ))}
            </Select>
            {petStore.myPets.length === 0 && (
              <small style={{ color: '#888', marginTop: 8, display: 'block' }}>
                {t('services.detail.noAnimals')} <a onClick={() => navigate('/pets/add')} style={{ cursor: 'pointer' }}>{t('services.detail.addAnimal')}</a> {t('services.detail.toProfile')}.
              </small>
            )}
          </div>

          <div className="form-group">
            <label>{t('services.detail.notes')}</label>
            <TextArea
              rows={3}
              placeholder={t('services.detail.purchaseNotesPlaceholder')}
              value={customerNotes}
              onChange={(e) => setCustomerNotes(e.target.value)}
              maxLength={500}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
});

export default ServiceDetailPage;
